import { useRef, useState } from "react";
import { ArrowRight, Mic, Play, RotateCcw } from "lucide-react";
import { scrollToId } from "@/App";
import { Reveal, SectionTag } from "./Reveal";

const DEMOS = [
  { title: "Aria — Real-Time AI Conversation", poster: "/assets/demo-posters/aria-demo.jpg", video: "/assets/videos/aria-demo.mp4" },
  { title: "Maya — Natural AI Interaction", poster: "/assets/demo-posters/maya-demo.jpg", video: "/assets/videos/maya-demo.mp4" },
  { title: "Naomi — Emotional Conversation", poster: "/assets/demo-posters/naomi-demo.jpg", video: "/assets/videos/naomi-demo.mp4" },
  { title: "Ethan — AI Companion Experience", poster: "/assets/demo-posters/ethan-demo.jpg", video: "/assets/videos/ethan-demo.mp4" },
];

function CompanionCard({ title, poster, video, index }) {
  const videoRef = useRef(null);
  const [state, setState] = useState("poster"); // poster | playing | ended

  const start = () => {
    const v = videoRef.current;
    if (!v) return;
    const result = v.play();
    if (result) result.catch(() => setState("poster"));
  };

  const replay = () => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = 0;
    start();
  };

  const playing = state === "playing";
  const ended = state === "ended";

  return (
    <div data-testid={`cdemo-card-${index}`} className="group relative overflow-hidden rounded-xl border border-white/10">
      <img
        src={poster}
        alt={`${title} — concept demo poster`}
        style={{ objectPosition: "50% 20%" }}
        className={`aspect-video w-full object-cover transition-all duration-700 ${
          playing ? "opacity-0" : "opacity-50 group-hover:scale-[1.03] group-hover:opacity-65"
        }`}
      />
      <video
        ref={videoRef}
        data-testid={`cdemo-video-${index}`}
        src={video}
        poster={poster}
        preload="metadata"
        playsInline
        controls={playing}
        onPlay={() => setState("playing")}
        onEnded={() => setState("ended")}
        onError={() => setState("poster")}
        style={{ objectPosition: "50% 20%" }}
        className={`absolute inset-0 h-full w-full bg-black object-contain transition-opacity duration-500 ${
          state === "poster" ? "pointer-events-none opacity-0" : "opacity-100"
        }`}
      />

      <div
        className={`absolute inset-x-0 bottom-0 flex items-end justify-between p-5 transition-opacity duration-300 ${
          playing ? "pointer-events-none opacity-0" : "opacity-100"
        }`}
      >
        <p className="font-display text-base font-medium tracking-tight text-white">{title}</p>
        {ended ? (
          <button
            data-testid={`cdemo-replay-${index}`}
            onClick={replay}
            aria-label={`Replay ${title}`}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-cyan-300/50 bg-black/60 text-cyan-200 backdrop-blur-md transition-colors duration-300 hover:bg-cyan-300/20"
          >
            <RotateCcw size={15} />
          </button>
        ) : (
          <button
            data-testid={`cdemo-play-${index}`}
            onClick={start}
            aria-label={`Play ${title}`}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-cyan-300/25 bg-black/60 text-cyan-100/90 backdrop-blur-md transition-colors duration-300 hover:border-cyan-300/60 hover:text-cyan-200"
          >
            <Play size={15} className="ml-0.5" />
          </button>
        )}
      </div>
    </div>
  );
}

export default function Demo() {
  return (
    <section id="demo" data-testid="demo-section" className="relative py-16 lg:py-36">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <Reveal>
          <SectionTag>Demo</SectionTag>
          <h2 className="font-display mt-5 max-w-3xl text-3xl font-light tracking-tighter text-white sm:text-4xl lg:text-5xl">
            Experience the <span className="text-cyan-300">AI Companion</span> Concept
          </h2>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#C5CBD5]">
            See how the planned AIROBOTCAMSTUDIO Level 1 experience is intended to feel through
            short AI videochat concept demonstrations.
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <div
            data-testid="demo-aria-live-strip"
            className="mt-12 flex flex-col items-start gap-5 rounded-2xl border border-cyan-300/25 bg-cyan-300/[0.04] p-6 sm:flex-row sm:items-center"
          >
            <img
              src="/assets/aria-live.jpg"
              alt="Aria — the live AI companion available in the early preview"
              className="h-16 w-16 shrink-0 rounded-xl border border-white/10 object-cover"
            />
            <div className="min-w-0 flex-1">
              <p className="font-display text-[10px] font-semibold uppercase tracking-[0.3em] text-cyan-300/90">
                Live Early Preview · AI Companion
              </p>
              <p className="mt-2 text-sm leading-relaxed text-[#C5CBD5]">
                Aria is live for approved testers while Level 1 remains in development.
              </p>
            </div>
            <button
              data-testid="demo-aria-live-cta"
              onClick={() => scrollToId("aria-live")}
              className="group inline-flex shrink-0 items-center gap-2 rounded-md bg-cyan-300 px-6 py-3.5 text-sm font-bold uppercase tracking-[0.1em] text-black transition-transform duration-300 hover:-translate-y-0.5 hover:bg-cyan-200 active:scale-[0.98]"
            >
              <Mic size={15} />
              Talk to Aria
            </button>
          </div>
        </Reveal>

        <div className="mt-14 grid gap-5 md:grid-cols-2">
          {DEMOS.map(({ title, poster, video }, i) => (
            <Reveal key={title} delay={0.06 * i}>
              <CompanionCard title={title} poster={poster} video={video} index={i} />
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.2}>
          <p data-testid="demo-disclosure" className="mt-10 text-xs leading-relaxed text-white/55">
            Pre-rendered concept demonstrations. Real-time MVP currently in development.
          </p>
          <a
            href="/register"
            data-testid="demo-register-cta"
            className="group mt-6 inline-flex items-center gap-2 rounded-md border border-cyan-300/30 px-6 py-3 text-sm font-semibold text-cyan-200/90 transition-all duration-300 hover:-translate-y-0.5 hover:border-cyan-300/60 hover:text-cyan-100"
          >
            Register for Early Access
            <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
          </a>
        </Reveal>
      </div>
    </section>
  );
}
